import { useState } from 'react'
import { Link, router } from '@inertiajs/react'
import { DashboardLayout } from '@/layouts/DashboardLayout'
import { Button } from '@/components/ui/button'
import { LockedButton } from '@/components/billing/LockedButton'
import { SyncBranchModal } from '@/components/SyncBranchModal'
import { Plus, Trash2, Phone, Mail, Copy, Check, Clock, Calendar, Pencil, RotateCcw } from 'lucide-react'
import type { PageProps, StaffMember } from '@/types'

interface TeamIndexProps extends PageProps {
  staff: StaffMember[]
  can_create?: boolean
  can_edit?: boolean
  can_delete?: boolean
  current_staff_id?: number | null
  has_multiple_businesses?: boolean
}

export default function TeamIndex({
  staff,
  can_create = true,
  can_edit = true,
  can_delete = true,
  current_staff_id,
  has_multiple_businesses = false,
  current_user,
  current_organization,
  current_business,
  flash,
  billing,
}: TeamIndexProps) {
  const orgSlug = current_organization?.slug || ''
  const [copiedId, setCopiedId] = useState<number | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<StaffMember | null>(null)
  
  const staffLimit = billing?.limits?.staff
  const staffLimitReached = staffLimit !== null && staffLimit !== undefined && staff.length >= staffLimit

  const handleDelete = (member: StaffMember) => {
    if ((member.sibling_count ?? 0) > 0) {
      setDeleteTarget(member)
      return
    }
    const msg = member.pending
      ? `¿Cancelar la invitación de "${member.name}"?`
      : `¿Eliminar a "${member.name}" del equipo?`
    
    if (confirm(msg)) {
      router.delete(`/${orgSlug}/team/${member.id}`)
    }
  }

  const confirmDelete = (syncAll: boolean) => {
    if (!deleteTarget) return
    router.delete(`/${orgSlug}/team/${deleteTarget.id}`, {
      data: { sync_all: syncAll },
    })
    setDeleteTarget(null)
  }

  const renewInvite = (member: StaffMember) => {
    router.post(`/${orgSlug}/team/${member.id}/renew`)
  }

  const copyInviteLink = async (member: StaffMember) => {
    if (!member.invite_token) return
    
    const link = `${window.location.origin}/invite/${member.invite_token}`
    
    try {
      await navigator.clipboard.writeText(link)
      setCopiedId(member.id)
      setTimeout(() => setCopiedId(null), 2000)
    } catch {
      // Fallback for older browsers or non-HTTPS
      const textArea = document.createElement('textarea')
      textArea.value = link
      textArea.style.position = 'fixed'
      textArea.style.left = '-9999px'
      document.body.appendChild(textArea)
      textArea.select()
      document.execCommand('copy')
      document.body.removeChild(textArea)
      setCopiedId(member.id)
      setTimeout(() => setCopiedId(null), 2000)
    }
  }

  const getRoleBadge = (role: string) => {
    if (role === 'admin') {
      return (
        <span className="rounded-full bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary">
          Administrador
        </span>
      )
    }
    return (
      <span className="rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground">
        Personal
      </span>
    )
  }

  return (
    <DashboardLayout
      currentUser={current_user}
      currentOrganization={current_organization}
      currentBusiness={current_business}
      orgSlug={orgSlug}
    >
      <div className="space-y-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-foreground">Equipo</h1>
            <p className="text-muted-foreground">
              Administra los miembros de tu equipo
            </p>
          </div>
          {can_create && (
            staffLimitReached ? (
              <LockedButton
                isLocked={true}
                limitKey="staff"
                currentCount={staff.length}
                orgSlug={orgSlug}
                className="w-full sm:w-auto"
              >
                Mejorar plan
              </LockedButton>
            ) : (
              <Link href={`/${orgSlug}/team/new`} className="w-full sm:w-auto">
                <Button className="w-full sm:w-auto">
                  <Plus className="mr-2 h-4 w-4" />
                  Invitar miembro
                </Button>
              </Link>
            )
          )}
        </div>

        {flash?.notice && (
          <div className="rounded-lg bg-green-500/10 p-3 text-sm text-green-500">
            {flash.notice}
          </div>
        )}

        {flash?.alert && (
          <div className="rounded-lg bg-destructive/10 p-3 text-sm text-destructive">
            {flash.alert}
          </div>
        )}

        {staff.length === 0 ? (
          <div className="rounded-xl border border-border bg-card p-12 text-center">
            <p className="text-muted-foreground">
              No tienes miembros en tu equipo
            </p>
            {staffLimitReached ? (
              <div className="mt-4">
                <LockedButton
                  isLocked={true}
                  limitKey="staff"
                  currentCount={staff.length}
                  orgSlug={orgSlug}
                  variant="outline"
                >
                  Mejorar plan
                </LockedButton>
              </div>
            ) : (
              <Link href={`/${orgSlug}/team/new`} className="mt-4 inline-block">
                <Button variant="outline">
                  <Plus className="mr-2 h-4 w-4" />
                  Invitar a tu primer miembro
                </Button>
              </Link>
            )}
          </div>
        ) : (
          <div className="rounded-xl border border-border bg-card">
            <div className="divide-y divide-border">
              {staff.map((member) => (
                <div
                  key={member.id}
                  className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between"
                >
                  <Link
                    href={`/${orgSlug}/team/${member.id}`}
                    className="flex min-w-0 items-center gap-4 rounded-lg hover:bg-accent/50"
                  >
                    <div
                      className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full text-sm font-medium text-white"
                      style={{ backgroundColor: member.color || '#6366f1' }}
                    >
                      {member.name?.charAt(0).toUpperCase() || '?'}
                    </div>
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <h3 className="font-medium text-card-foreground truncate">
                          {member.name}
                        </h3>
                        {getRoleBadge(member.role)}
                        {member.pending && (
                          <span className={`flex items-center gap-1 rounded-full px-2 py-0.5 text-xs ${member.expired ? 'bg-destructive/10 text-destructive' : 'bg-yellow-500/10 text-yellow-500'}`}>
                            <Clock className="h-3 w-3" />
                            {member.expired ? 'Expirada' : 'Pendiente'}
                          </span>
                        )}
                      </div>
                      <div className="flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <Phone className="h-3.5 w-3.5 flex-shrink-0" />
                          <span className="truncate">{member.phone}</span>
                        </span>
                        {member.email && (
                          <span className="flex items-center gap-1">
                            <Mail className="h-3.5 w-3.5 flex-shrink-0" />
                            <span className="truncate">{member.email}</span>
                          </span>
                        )}
                      </div>
                    </div>
                  </Link>
                  <div className="flex items-center gap-2 pl-14 sm:pl-0">
                    {can_create && member.pending && member.invite_token && !member.expired && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => copyInviteLink(member)}
                      >
                        {copiedId === member.id ? (
                          <>
                            <Check className="mr-1 h-4 w-4 text-green-500" />
                            Copiado
                          </>
                        ) : (
                          <>
                            <Copy className="mr-1 h-4 w-4" />
                            Copiar link
                          </>
                        )}
                      </Button>
                    )}
                    {can_create && member.pending && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => renewInvite(member)}
                      >
                        <RotateCcw className="mr-1 h-4 w-4" />
                        Renovar enlace
                      </Button>
                    )}
                    {!member.pending && (
                      <Link href={`/${orgSlug}/team/${member.id}/availability`}>
                        <Button variant="outline" size="sm">
                          <Calendar className="mr-1 h-4 w-4" />
                          Horarios
                          {member.has_custom_hours && (
                            <span className="ml-1 h-2 w-2 rounded-full bg-primary" />
                          )}
                        </Button>
                      </Link>
                    )}
                    {(can_edit || member.id === current_staff_id) && !member.pending && (
                      <Link href={`/${orgSlug}/team/${member.id}/edit`}>
                        <Button variant="ghost" size="sm">
                          <Pencil className="h-4 w-4" />
                        </Button>
                      </Link>
                    )}
                    {can_delete && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleDelete(member)}
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <SyncBranchModal
        open={!!deleteTarget}
        title="Eliminar miembro"
        description={`"${deleteTarget?.name}" existe en ${deleteTarget?.sibling_count ?? 0} sucursal(es) más. ¿Deseas eliminarlo de todas?`}
        confirmLabel="Eliminar de todas las sucursales"
        singleLabel="Solo de esta sucursal"
        onSyncAll={() => confirmDelete(true)}
        onSingleOnly={() => confirmDelete(false)}
        onCancel={() => setDeleteTarget(null)}
      />
    </DashboardLayout>
  )
}
